import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { mediaQueries } from "~/foundation/styles/breakpoints";

export type ActiveDialog =
  | { kind: "newDocument" }
  | { kind: "newCollection" }
  | { kind: "newProject" }
  | { kind: "renameDocument"; documentId: string }
  | { kind: "deleteDocument"; documentId: string }
  | { kind: "shareDocument"; documentId: string }
  /** W10 §1 — collection + parent picker. */
  | { kind: "moveDocument"; documentId: string }
  | { kind: "welcomeGuide" }
  | { kind: "shortcuts" }
  | null;

export interface ShellState {
  /** Desktop: 240px sidebar collapsed to the 48px rail. */
  sidebarCollapsed: boolean;
  /** Mobile only: the drawer. Never persisted — a reload should not open a drawer. */
  mobileSidebarOpen: boolean;
  collapsedSections: Record<string, boolean>;
  /** Sidebar collection tree: which collections show their documents. */
  expandedCollections: Record<string, boolean>;
  /** Sidebar project tree (W5): which projects show their collections. */
  expandedProjects: Record<string, boolean>;
  /** Sidebar collection tree: which documents show their child documents. */
  expandedDocuments: Record<string, boolean>;
  /** Desktop sidebar width in px, user-resizable within the clamp bounds. Persisted. */
  sidebarWidth: number;
  /** True only while the resize handle is being dragged — suspends the width transition. */
  sidebarResizing: boolean;
  commandBarOpen: boolean;
  activeDialog: ActiveDialog;
  /**
   * The entity the current scene is "about" (an open item). Entity-scoped actions read it,
   * which is how Rename/Delete appear in the command bar while an item is on screen.
   */
  activeEntityId: string | null;
  /** Last few action ids, newest first — the command bar's "recent" group. */
  recentActionIds: string[];
  /**
   * Pinned favourites (layered nav, 2026-09-09): destination ids from `navCatalog` in pin
   * order. Persisted — pins are a habit, not a session.
   */
  pinnedNavIds: string[];
  /** The sidebar shows the sections layer (the launcher) instead of the workspace nav. Never persisted. */
  navLayerOpen: boolean;
  /**
   * W7 — the Ask bar. Two fields are the WHOLE seam between the two palettes: kms needed a
   * pub/sub module (`askMode.ts`, a `Set<Listener>`) only because its two components could not
   * see each other's state. Here they share a store, so there is nothing to publish.
   *
   * `openAskBar` closes the command bar in the SAME `set()` — the invariant kms expressed as
   * `kbar.setVisualState("hidden")`. One palette on screen, always.
   */
  askBarOpen: boolean;
  /** Whatever was typed in ⌘K when the person fell through to "Ask AI". */
  askBarPrefill: string;
  /**
   * W8 — the document right drawer. kms mounted EIGHT mutually-exclusive right panels, each
   * gated on its own boolean and each later flag negating the earlier ones. One slot with a
   * `kind` makes that exclusion structural: opening one closes the others because there is
   * nowhere else for them to be.
   *
   * Never persisted — a reload should not reopen a drawer; the URL is what makes a
   * conversation linkable. The WIDTH is persisted, separately (`rightDrawerWidth`), and
   * deliberately does not sync across windows: kms learned that "sidebar widths cause fighting
   * between windows".
   */
  rightDrawer: { kind: RightDrawerKind | null; entityId: string | null; resizing: boolean };
  rightDrawerWidth: number;

  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  setMobileSidebarOpen: (open: boolean) => void;
  toggleSection: (sectionId: string) => void;
  /** Explicit form, for a section whose default is collapsed (absent key ≠ open). */
  setSectionCollapsed: (sectionId: string, collapsed: boolean) => void;
  /** Explicit: an ancestor of the open document is shown open without a stored choice. */
  setDocumentExpanded: (documentId: string, expanded: boolean) => void;
  toggleCollectionExpanded: (collectionId: string) => void;
  toggleProjectExpanded: (projectId: string) => void;
  setSidebarWidth: (width: number) => void;
  resetSidebarWidth: () => void;
  setSidebarResizing: (resizing: boolean) => void;
  setCommandBarOpen: (open: boolean) => void;
  toggleCommandBar: () => void;
  openDialog: (dialog: NonNullable<ActiveDialog>) => void;
  closeDialog: () => void;
  setActiveEntityId: (entityId: string | null) => void;
  rememberAction: (actionId: string) => void;
  togglePinned: (destinationId: string) => void;
  setNavLayerOpen: (open: boolean) => void;
  openAskBar: (prefill?: string) => void;
  closeAskBar: () => void;
  /** One slot: passing a new `kind` replaces whatever was open. */
  openRightDrawer: (kind: RightDrawerKind, entityId?: string | null) => void;
  closeRightDrawer: () => void;
  setRightDrawerWidth: (width: number) => void;
  resetRightDrawerWidth: () => void;
  setRightDrawerResizing: (resizing: boolean) => void;
}

/**
 * The panels that can occupy the one right-hand slot. `conversation` is W8's drawer; the rest
 * are named now so the next surface to want the slot extends a union instead of adding a
 * ninth boolean.
 */
export type RightDrawerKind =
  | "conversation"
  | "chat"
  | "comments"
  | "history"
  | "insights"
  | "document-ai";

const RECENT_LIMIT = 5;

/** Hard cap so the pinned bar stays a bookmarks strip, not a second sidebar. */
export const MAX_PINNED_NAV = 12;

/** Resizable sidebar bounds. The default matches the `--sidebar-width` token. */
export const SIDEBAR_DEFAULT_WIDTH = 240;
export const SIDEBAR_MIN_WIDTH = 200;
export const SIDEBAR_MAX_WIDTH = 480;

/** The right drawer's own bounds — kms's numbers, reproduced (W8 §B.1). */
export const RIGHT_DRAWER_DEFAULT_WIDTH = 300;
export const RIGHT_DRAWER_MIN_WIDTH = 216;
export const RIGHT_DRAWER_MAX_WIDTH = 600;

export function clampRightDrawerWidth(width: number): number {
  if (!Number.isFinite(width)) {
    return RIGHT_DRAWER_DEFAULT_WIDTH;
  }
  return Math.min(RIGHT_DRAWER_MAX_WIDTH, Math.max(RIGHT_DRAWER_MIN_WIDTH, Math.round(width)));
}

/** Persisted storage can hold anything (old versions, hand edits) — always read through this. */
export function clampSidebarWidth(width: number): number {
  if (!Number.isFinite(width)) {
    return SIDEBAR_DEFAULT_WIDTH;
  }
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(width)));
}

export const useShellStore = create<ShellState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      collapsedSections: {},
      expandedCollections: {},
      expandedProjects: {},
      expandedDocuments: {},
      sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
      sidebarResizing: false,
      commandBarOpen: false,
      activeDialog: null,
      activeEntityId: null,
      recentActionIds: [],
      pinnedNavIds: [],
      navLayerOpen: false,
      askBarOpen: false,
      askBarPrefill: "",
      rightDrawer: { kind: null, entityId: null, resizing: false },
      rightDrawerWidth: RIGHT_DRAWER_DEFAULT_WIDTH,

      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setMobileSidebarOpen: (mobileSidebarOpen) => set({ mobileSidebarOpen }),

      toggleSection: (sectionId) =>
        set((state) => ({
          collapsedSections: {
            ...state.collapsedSections,
            [sectionId]: !state.collapsedSections[sectionId],
          },
        })),

      setSectionCollapsed: (sectionId, collapsed) =>
        set((state) => ({
          collapsedSections: { ...state.collapsedSections, [sectionId]: collapsed },
        })),

      setDocumentExpanded: (documentId, expanded) =>
        set((state) => ({
          expandedDocuments: { ...state.expandedDocuments, [documentId]: expanded },
        })),

      toggleCollectionExpanded: (collectionId) =>
        set((state) => ({
          expandedCollections: {
            ...state.expandedCollections,
            [collectionId]: !state.expandedCollections[collectionId],
          },
        })),

      toggleProjectExpanded: (projectId) =>
        set((state) => ({
          expandedProjects: {
            ...state.expandedProjects,
            [projectId]: !state.expandedProjects[projectId],
          },
        })),

      setSidebarWidth: (width) => set({ sidebarWidth: clampSidebarWidth(width) }),
      resetSidebarWidth: () => set({ sidebarWidth: SIDEBAR_DEFAULT_WIDTH }),
      setSidebarResizing: (sidebarResizing) => set({ sidebarResizing }),

      setCommandBarOpen: (commandBarOpen) => set({ commandBarOpen }),
      toggleCommandBar: () => set((state) => ({ commandBarOpen: !state.commandBarOpen })),

      openDialog: (activeDialog) => set({ activeDialog, commandBarOpen: false }),
      closeDialog: () => set({ activeDialog: null }),
      setActiveEntityId: (activeEntityId) => set({ activeEntityId }),

      rememberAction: (actionId) =>
        set((state) => ({
          recentActionIds: [
            actionId,
            ...state.recentActionIds.filter((id) => id !== actionId),
          ].slice(0, RECENT_LIMIT),
        })),

      togglePinned: (destinationId) =>
        set((state) => ({
          pinnedNavIds: state.pinnedNavIds.includes(destinationId)
            ? state.pinnedNavIds.filter((id) => id !== destinationId)
            : [...state.pinnedNavIds, destinationId].slice(-MAX_PINNED_NAV),
        })),
      setNavLayerOpen: (navLayerOpen) => set({ navLayerOpen }),

      // One palette on screen: opening Ask closes Commands in the same update.
      openAskBar: (prefill = "") =>
        set({ askBarOpen: true, askBarPrefill: prefill, commandBarOpen: false }),
      closeAskBar: () => set({ askBarOpen: false, askBarPrefill: "" }),

      // One slot, so mutual exclusion needs no bookkeeping: this IS the eight booleans.
      openRightDrawer: (kind, entityId = null) =>
        set({ rightDrawer: { kind, entityId, resizing: false } }),
      closeRightDrawer: () => set({ rightDrawer: { kind: null, entityId: null, resizing: false } }),
      setRightDrawerWidth: (width) => set({ rightDrawerWidth: clampRightDrawerWidth(width) }),
      resetRightDrawerWidth: () => set({ rightDrawerWidth: RIGHT_DRAWER_DEFAULT_WIDTH }),
      setRightDrawerResizing: (resizing) =>
        set((state) => ({ rightDrawer: { ...state.rightDrawer, resizing } })),
    }),
    {
      name: "slate.shell",
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        collapsedSections: state.collapsedSections,
        expandedCollections: state.expandedCollections,
        expandedProjects: state.expandedProjects,
        expandedDocuments: state.expandedDocuments,
        sidebarWidth: state.sidebarWidth,
        recentActionIds: state.recentActionIds,
        pinnedNavIds: state.pinnedNavIds,
        // The drawer's WIDTH is a habit; which drawer was open is not. Persisting `kind` would
        // reopen a panel on a reload the person never asked for.
        rightDrawerWidth: state.rightDrawerWidth,
      }),
    },
  ),
);

export type ShellStore = typeof useShellStore;

function isMobileViewport(): boolean {
  return typeof window !== "undefined" && window.matchMedia(mediaQueries.mobile).matches;
}

/**
 * The one "toggle navigation" verb the console bar and the ⌘. shortcut both call. Which
 * piece of state it moves is a viewport question, and it is answered here so no component
 * has to ask it twice.
 */
export function toggleNavigation(): void {
  const state = useShellStore.getState();
  if (isMobileViewport()) {
    state.setMobileSidebarOpen(!state.mobileSidebarOpen);
  } else {
    state.toggleSidebar();
  }
}
